import { makeSuccessToast, makeErrorToast, msg } from '../../utils/makeToast'
import { createSlice, createAsyncThunk, nanoid, Reducer, PayloadAction } from '@reduxjs/toolkit'
import { isEqual, orderBy } from 'lodash'
import { API, graphqlOperation } from '@aws-amplify/api'
import * as mutations from '../../graphql/mutations'
import * as queries from '../../graphql/queries'
import projectChildrenMergeSort from '../../utils/projectChildrenMergeSort'
import { getProjectTotals } from '../../utils/projectUtil'
import {
  fetchArchiveProjectsByTenantIdUtil,
  fetchFreelancerProjectUtil,
  fetchProjectsByClientIdUtil,
  fetchProjectsByParentProjectIdUtil,
  fetchProjectsByTenantIdUtil,
  fetchQuotesByParentProjectIdUtil,
  getFetchFilter,
} from '../../utils/fetchUtils'
import i18next from 'i18next'
import {
  GetClientsQueryApi,
  GetFreelancersByEmailQueryApi,
  GetFreelancersByIdQueryApi,
  GetProjectsQueryApi,
  ReduxFreelancers,
  ReduxProjects,
  ReduxQuote,
} from '@src/types/apiType'
import { RootState, ThunkAppDispatch } from '@src/redux/store'
import { UpdateProjectsInput } from '@src/API'

type ApiStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'rejected'

interface CategoryTotals {
  isInternational: boolean
  categoryPrefix: string
  categoryRoman: string
  categoryId: number
  category: string
  category_fr: string
  budgetTotal: number
  provisionalTotal: number
  actualTotal: number
}

interface Totals {
  budgetSubTotal: number
  budgetGrandTotal: number
  budgetMargin: number
  budgetMarginPercentage: number
  provisionalSubTotal: number
  provisionalGrandTotal: number
  provisionalMargin: number
  provisionalMarginPercentage: number
  actualSubTotal: number
  actualGrandTotal: number
  actualMargin: number
  actualMarginPercentage: number
}

interface Discount {
  totalAfterDiscount: number
  totalDiscount: number
  budgetTotalDiscountOnMargin: number
  budgetTotalDiscountOnMarginPercentage: number
  budgetTotalDiscountPercentage: number
  provisionalTotalDiscountOnMargin: number
  provisionalTotalDiscountOnMarginPercentage: number
  provisionalTotalDiscountPercentage: number
  actualTotalDiscountOnMargin: number
  actualTotalDiscountOnMarginPercentage: number
  actualTotalDiscountPercentage: number
}

interface ProjectTotals {
  id: string | null
  name: string | null
  quotes?: CategoryTotals[]
  categoryTotals?: CategoryTotals[]
  totals?: Totals | {}
  franceTotals?: Totals | {}
  internationalTotals?: Totals | {}
  discount?: Discount | null
  franceCategoryTotals?: CategoryTotals[]
  internationalCategoryTotals?: CategoryTotals[]
}

interface ProjectState {
  projectData: ReduxProjects[]
  projectChildrenData: (ReduxProjects | ReduxQuote)[]
  freelanceEmail: ReduxFreelancers[]
  projectTotals: ProjectTotals | null
  summaryQuoteId: string
  quoteType: string
  fetchProjectApiStatus: ApiStatus
  fetchProjectChildrenApiStatus: ApiStatus
  fetchProjectTotalsApiStatus: ApiStatus
  fetchFreelancerInfoApiStatus: ApiStatus
  createOrUpdateProjectApiStatus: ApiStatus
  deleteProjectApiStatus: ApiStatus
  updateFreelancerInfoStatus: ApiStatus
  removeFreelancerStatus: ApiStatus
  isDeleteDialogOpen: boolean
  projectModalOpenStatus: boolean
  projectDeleteDialogOpenStatus: boolean
  isFreelancerModalOpen: boolean
  currentProject: ReduxProjects | null
  error: any | null
}

const initialState: ProjectState = {
  projectData: [],
  projectChildrenData: [],
  freelanceEmail: [],
  projectTotals: null,
  summaryQuoteId: '',
  quoteType: '',
  fetchProjectApiStatus: 'idle',
  fetchProjectChildrenApiStatus: 'idle',
  fetchProjectTotalsApiStatus: 'idle',
  fetchFreelancerInfoApiStatus: 'idle',
  createOrUpdateProjectApiStatus: 'idle',
  deleteProjectApiStatus: 'idle',
  updateFreelancerInfoStatus: 'idle',
  removeFreelancerStatus: 'idle',
  isDeleteDialogOpen: false,
  projectModalOpenStatus: false,
  projectDeleteDialogOpenStatus: false,
  isFreelancerModalOpen: false,
  currentProject: null,
  error: null,
}

// Only fetches project on clients/:clientsId page only companyAdmin can view this page
export const fetchProject = createAsyncThunk<
  ReduxProjects[],
  { parentClientId: string },
  {
    state: RootState
    rejectValue: string
  }
>('project/fetchProject', async ({ parentClientId }, { getState, rejectWithValue }) => {
  try {
    const { auth, settings } = getState()
    const projectFilter = getFetchFilter(auth.userInfo)
    const sort = settings.sort
    const items: ReduxProjects[] = await fetchProjectsByClientIdUtil(parentClientId, projectFilter)

    // For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
    let sortedProjects: ReduxProjects[] = []
    if (sort === 'date') {
      sortedProjects = orderBy(items, ['createdOn'], ['asc'])
    } else if (sort === 'dateM') {
      sortedProjects = orderBy(items, ['createdOn'], ['desc'])
    } else if (sort === 'name') {
      sortedProjects = orderBy(items, [project => project?.name?.toLowerCase()], ['asc'])
    }
    if (sortedProjects) {
      return sortedProjects
    } else {
      makeErrorToast(msg)
      return rejectWithValue('Error Fetching Project')
    }
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

export const fetchFreelancerProject = createAsyncThunk<
  ReduxProjects[],
  void,
  {
    state: RootState
    rejectValue: string
  }
>('project/fetchFreelancerProject', async (arg, { getState, rejectWithValue }) => {
  // Todo remove this any after auth slice has been changed to typescript

  const { userInfo }: any = getState().auth
  const { settings } = getState()

  const sort = settings.sort
  try {
    const items = await fetchFreelancerProjectUtil(userInfo?.tenantId)
    // For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
    let sortedProjects: ReduxProjects[] = []
    if (sort === 'date') {
      sortedProjects = orderBy(items, ['createdOn'], ['asc'])
    } else if (sort === 'dateM') {
      sortedProjects = orderBy(items, ['createdOn'], ['desc'])
    } else if (sort === 'name') {
      sortedProjects = orderBy(items, [project => project.name.toLowerCase()], ['asc'])
    }
    if (sortedProjects) {
      return sortedProjects
    } else {
      makeErrorToast(msg)
      return rejectWithValue('Error Fetching Project')
    }
  } catch (err: any) {
    console.log('err: ', err)
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

// fetches project & quote children for parentProjectID from project/:projectId page both companyAdmin & freelancer can see this
export const fetchProjectChildren = createAsyncThunk<
  (ReduxProjects | ReduxQuote)[],
  { parentProjectId: string },
  {
    state: RootState
    rejectValue: string
  }
>('project/fetchProjectChildren', async ({ parentProjectId }, { getState, rejectWithValue }) => {
  try {
    const { auth, settings } = getState()
    const filter = getFetchFilter(auth.userInfo)
    const sort = settings.sort

    const projectItems: ReduxProjects[] = await fetchProjectsByParentProjectIdUtil(
      parentProjectId,
      filter
    )

    const quoteItems: ReduxQuote[] = await fetchQuotesByParentProjectIdUtil(parentProjectId, filter)

    const projectChildrenData = [...projectItems, ...quoteItems]
    let sortedProjectChildren = projectChildrenData
    if (sort === 'date') {
      sortedProjectChildren = orderBy(projectChildrenData, ['createdOn'], ['asc'])
    } else if (sort === 'dateM') {
      sortedProjectChildren = orderBy(projectChildrenData, ['createdOn'], ['desc'])
    } else if (sort === 'name') {
      sortedProjectChildren = orderBy(
        projectChildrenData,
        [projectChild => projectChild?.name?.toLowerCase()],
        ['asc']
      )
    }
    return sortedProjectChildren
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

export const fetchProjectTotals = createAsyncThunk<
  ProjectTotals,
  string,
  {
    state: RootState
    rejectValue: string
  }
>('project/fetchProjectTotals', async (projectId, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState()
    const fetchFilter = getFetchFilter(auth.userInfo)
    const projectTotals: ProjectTotals = await getProjectTotals({ projectId, fetchFilter })
    console.log('projectTotals: ', projectTotals)
    return projectTotals
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

//  Logic to get freelancerdata
export const fetchFreelancerInfo = createAsyncThunk<
  ReduxFreelancers[],
  { freelancers: string[] },
  {
    rejectValue: string
  }
>('project/fetchFreelancerInfo', async ({ freelancers }, { rejectWithValue }) => {
  try {
    // Since we are addimng dummyId for all freelancers we need to
    //  remove that from freelancers when searching for email
    const removeDummyIdFromFreelancersArray = freelancers.filter(i => i !== 'dummyId')
    let freelanceEmail = [] as ReduxFreelancers[]
    // Only check further if freelancers array has other elements other than dummyId
    if (!removeDummyIdFromFreelancersArray) {
      return freelanceEmail
    }
    await Promise.all(
      removeDummyIdFromFreelancersArray.map(async freelancersId => {
        const result = (await API.graphql(
          graphqlOperation(queries.freelancersById, { freelancerTenantId: freelancersId })
        )) as { data: GetFreelancersByIdQueryApi }
        // Telling typescript we have the particular nested object items
        if (
          result?.data?.freelancersById?.items &&
          result?.data?.freelancersById?.items?.length > 0 &&
          result?.data?.freelancersById?.items[0]
        ) {
          freelanceEmail.push(result?.data?.freelancersById?.items[0])
        }
      })
    )
    return freelanceEmail
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

// Can be called from both clients/:clientsId & project/:projectId
// On client page only companyAdmin can access
// On project companyAdmin & freelancer can access
export const createProject = createAsyncThunk<
  void,
  { parentClientId: string | null; parentProjectId: string | null; projectName: string },
  {
    state: RootState
    dispatch: ThunkAppDispatch
    rejectValue: string
  }
>(
  'project/createProject',
  async (
    { parentClientId = null, parentProjectId = null, projectName },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const { auth } = getState()
      const projectFilter = getFetchFilter(auth.userInfo)
      if (projectName.length === 0) {
        makeErrorToast('Project Name cannot be empty')
        return rejectWithValue('Project Name cannot be empty')
      }
      // Search for the tenantId from it's parent client or parent  project id
      if (parentClientId) {
        const {
          data: { getClients: parentClientData },
        } = (await API.graphql(graphqlOperation(queries.getClients, { id: parentClientId }))) as {
          data: GetClientsQueryApi
        }
        const projectData: ReduxProjects[] = await fetchProjectsByClientIdUtil(
          parentClientId,
          projectFilter
        )

        if (projectData.some(e => e.name === projectName)) {
          makeErrorToast('Project Name already exists!')
          return rejectWithValue('Project Name already exists!')
        }

        const projectDetails = {
          id: nanoid(),
          name: projectName,
          tenantId: parentClientData?.tenantId,
          activeFlag: true,
          type: 'project',
          parentClientId,
          freelancers: ['dummyId'],
          childFreelancers: ['dummyId'],
          offlineIds: ['dummyId'],
        }

        await API.graphql(
          graphqlOperation(mutations.createProjects, {
            input: projectDetails,
          })
        )

        dispatch(fetchProject({ parentClientId }))

        return
      }
      if (parentProjectId) {
        // const parsedParentProjectData = JSON.parse(JSON.stringify(parentProjectData))
        const {
          data: { getProjects: parentProjectData },
        } = (await API.graphql(
          graphqlOperation(queries.getProjects, {
            id: parentProjectId,
          })
        )) as { data: GetProjectsQueryApi }

        const projectData = await fetchProjectsByParentProjectIdUtil(parentProjectId, projectFilter)

        if (projectData.some(e => e.name === projectName)) {
          makeErrorToast('Project Name Already Exists')
          return rejectWithValue('Project Name already exists!')
        }

        const projectDetails = {
          id: nanoid(),
          name: projectName,
          tenantId: parentProjectData?.tenantId,
          activeFlag: true,
          type: 'project',
          parentProjectId,
          freelancers: ['dummyId'],
          childFreelancers: [...(parentProjectData?.childFreelancers || ['dummyId'])],
          offlineIds: ['dummyId'],
        }
        await API.graphql(graphqlOperation(mutations.createProjects, { input: projectDetails }))

        dispatch(fetchProjectChildren({ parentProjectId }))
        return
      }
    } catch (err: any) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

//  Logic to add freelancerdata
export const addFreelancerToProject = createAsyncThunk<
  void,
  { freelanceEmail: string },
  { state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
  'project/addFreelancerToProject',
  async ({ freelanceEmail }, { getState, dispatch, rejectWithValue }) => {
    try {
      // 1. Get freelancId from Freelance table
      // 2. Custom resolver send request
      const data = (await API.graphql(
        graphqlOperation(queries.freelancersByEmail, { email: freelanceEmail })
      )) as { data: GetFreelancersByEmailQueryApi }
      if (
        data?.data?.freelancersByEmail?.items &&
        data?.data?.freelancersByEmail?.items?.length > 0 &&
        data?.data?.freelancersByEmail?.items[0]
      ) {
        const freelancerId = data.data.freelancersByEmail.items[0]?.freelancerTenantId
        // Check if freelancer email exists
        if (!freelancerId) {
          makeErrorToast(
            'The User with the given email ID was not found!. Please contact Chedar Support for further information.'
          )
          return rejectWithValue('The User with the given email ID was not found!')
        }
        // Check if freelancer exists in project
        const { projects } = getState()
        const currentProject = projects.currentProject
        const projectId = currentProject?.id
        // Check if freelancer is added to project or parent Project
        if (
          currentProject?.freelancers?.includes(freelancerId) ||
          currentProject?.childFreelancers?.includes(freelancerId)
        ) {
          makeErrorToast('User is already added to the Project')
          return rejectWithValue('User is already added to the Project')
        }

        // Check if comapny Tenant id exists if not add it to the freelance table
        const { userInfo }: any = getState().auth
        if (
          !data?.data?.freelancersByEmail?.items[0]?.companyTenantId?.includes(userInfo?.tenantId)
        ) {
          // Destructure comanyTenantId if other ids exist  and add the current users company Tenant Id.
          const companyTenantId = [
            ...(data?.data?.freelancersByEmail?.items[0].companyTenantId
              ? data?.data?.freelancersByEmail?.items[0]?.companyTenantId
              : []),
            userInfo?.tenantId,
          ]

          await API.graphql(
            graphqlOperation(mutations.updateFreelancers, {
              input: {
                id: data?.data?.freelancersByEmail?.items[0].id,
                companyTenantId,
                _version: data?.data?.freelancersByEmail?.items[0]._version,
              },
            })
          )
        }
        // Mutaion for custom Resolver to add freelancer to project and all child projects and quotes
        await API.graphql(
          graphqlOperation(mutations.addFreelancerToProject, {
            projectId,
            freelancerId,
          })
        )
        if (currentProject?.parentClientId) {
          dispatch(fetchProject({ parentClientId: currentProject.parentClientId }))
        } else if (currentProject?.parentProjectId) {
          dispatch(fetchProjectChildren({ parentProjectId: currentProject.parentProjectId }))
        }
      } else {
        makeErrorToast(`Freelancer account ${freelanceEmail} is  not active`)
        return rejectWithValue('Freelancer account is not active')
      }

      makeSuccessToast('Freelancer Successfully Added')
      return
    } catch (err: any) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updateProject = createAsyncThunk<
  void,
  { project: ReduxProjects; projectName: string },
  { state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
  'project/updateProject',
  async ({ project, projectName }, { dispatch, getState, rejectWithValue }) => {
    // Gets the original Project from datastore then updates it
    try {
      const { auth } = getState()
      const filter = getFetchFilter(auth.userInfo)
      const {
        data: { getProjects: originalProject },
      } = (await API.graphql(
        graphqlOperation(queries.getProjects, {
          id: project.id,
          filter,
        })
      )) as { data: GetProjectsQueryApi }
      // We check if we have access to the project
      if (originalProject && project.id) {
        let projectUpdateparams: UpdateProjectsInput = {
          id: project.id,
          name: projectName,
          _version: originalProject._version,
        }

        await API.graphql(
          graphqlOperation(mutations.updateProjects, { input: projectUpdateparams })
        )
      } else {
        rejectWithValue(`Can't access project`)
      }

      if (!!project.parentClientId) {
        dispatch(fetchProject({ parentClientId: project.parentClientId }))
        return
      }

      if (!!project.parentProjectId) {
        dispatch(fetchProjectChildren({ parentProjectId: project.parentProjectId }))
        return
      }
    } catch (err: any) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const deleteProject = createAsyncThunk<
  void,
  { project: ReduxProjects },
  {
    dispatch: ThunkAppDispatch
    rejectValue: string
  }
>('project/deleteProject', async ({ project }, { dispatch, rejectWithValue }) => {
  // Gets the original Project from datastore then deletes it along with all it's children project & quotes
  try {
    await API.graphql(graphqlOperation(mutations.deleteProjectById, { projectId: project.id }))
    if (!!project.parentClientId) {
      dispatch(fetchProject({ parentClientId: project.parentClientId }))
    }
    if (!!project.parentProjectId) {
      dispatch(fetchProjectChildren({ parentProjectId: project.parentProjectId }))
    }
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

export const createFreelancerArchiveProject = createAsyncThunk<
  void,
  { groups: string[]; tenantId: string },
  {
    dispatch: ThunkAppDispatch
    rejectValue: string
  }
>('project/createFreelancerArchiveProject', async (userInfo, { dispatch, rejectWithValue }) => {
  try {
    const items = await fetchArchiveProjectsByTenantIdUtil(userInfo.tenantId)

    if (userInfo.groups.includes('freelancer') && items.length === 0) {
      const projectDetails = {
        id: nanoid(),
        name: `Archive ${nanoid().substring(0, 4)}`,
        tenantId: userInfo.tenantId,
        activeFlag: true,
        type: 'archive',
        // parentClientId,
        freelancers: ['dummyId', userInfo.tenantId],
        childFreelancers: ['dummyId', userInfo.tenantId],
        offlineIds: ['dummyId'],
      }
      await API.graphql(
        graphqlOperation(mutations.createProjects, {
          input: projectDetails,
        })
      )

      dispatch(fetchFreelancerProject())
    }
  } catch (err: any) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
})

//  Logic to delete freelancerdata
export const removeFreelancerFromProject = createAsyncThunk<
  void,
  { projectId: string; freelancerId: string },
  {
    state: RootState
    dispatch: ThunkAppDispatch
    rejectValue: string
  }
>(
  'project/removeFreelancerFromProject',
  async ({ projectId, freelancerId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      const filter = getFetchFilter(auth.userInfo)
      // Delete freelancer from project with api call
      await API.graphql(
        graphqlOperation(mutations.removeFreelancerFromProject, {
          projectId,
          freelancerId,
        })
      )
      const {
        data: { getProjects: projectData },
      } = (await API.graphql(graphqlOperation(queries.getProjects, { id: projectId, filter }))) as {
        data: GetProjectsQueryApi
      }
      projectData?.parentClientId &&
        (await dispatch(fetchProject({ parentClientId: projectData.parentClientId })))
      projectData?.parentProjectId &&
        (await dispatch(fetchProjectChildren({ parentProjectId: projectData.parentProjectId })))
      await dispatch(setUpdateCurrentProject(projectData))
      makeSuccessToast(i18next.t('DeleteFreelancer:removedFreelancer'))
      await dispatch(fetchFreelancerInfo({ freelancers: projectData?.freelancers as string[] }))
    } catch (err: any) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setUpdateCurrentProject: (state, action) => {
      state.currentProject = action.payload
    },
    setIsDeleteDialogOpen: (state, action) => {
      state.isDeleteDialogOpen = action.payload
    },
    setSummaryQuoteId: (state, action) => {
      state.summaryQuoteId = action.payload
    },
    setQuoteType: (state, action) => {
      state.quoteType = action.payload
    },
    setProjectModalOpenStatus: (state, action) => {
      state.projectModalOpenStatus = action.payload.status
      state.currentProject = action.payload.project
    },
    setIsFreelancersModalOpen: (state, action) => {
      state.isFreelancerModalOpen = action.payload.status
      state.currentProject = action.payload.project
    },
    setProjectDeleteDialogOpenStatus: (state, action) => {
      state.projectDeleteDialogOpenStatus = action.payload.status
      state.currentProject = action.payload.project
    },
    sortProjects: (state, action) => {
      if (action.payload === 'date') {
        state.projectData = orderBy(state.projectData, ['createdOn'], ['asc'])
      } else if (action.payload === 'dateM') {
        state.projectData = orderBy(state.projectData, ['createdOn'], ['desc'])
      } else if (action.payload === 'name') {
        state.projectData = orderBy(
          state.projectData,
          [project => project?.name?.toLowerCase()],
          ['asc']
        )
      }
    },
    sortProjectChildren: (state, action) => {
      if (action.payload === 'date') {
        state.projectChildrenData = orderBy(state.projectChildrenData, ['createdOn'], ['asc'])
      } else if (action.payload === 'dateM') {
        state.projectChildrenData = orderBy(state.projectChildrenData, ['createdOn'], ['desc'])
      } else if (action.payload === 'name') {
        state.projectChildrenData = orderBy(
          state.projectChildrenData,
          [projectChild => projectChild?.name?.toLowerCase()],
          ['asc']
        )
      }
    },
    sortAndMegeProjectChildren: (state, action) => {
      let { projectChildren, sort } = action.payload
      let sortedProjectChildren = projectChildren
      if (sort === 'date') {
        sortedProjectChildren = projectChildrenMergeSort(projectChildren)
      } else if (sort === 'name') {
        sortedProjectChildren = orderBy(
          projectChildren,
          [projectChild => projectChild?.name?.toLowerCase()],
          ['asc']
        )
      }
      state.projectChildrenData = sortedProjectChildren
    },
    clearProjectsData: (state, action) => {
      state.projectData = []
      state.projectChildrenData = []
    },
    clearProjectsChildrenData: (state, action) => {
      state.projectChildrenData = []
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchProject.pending, (state, action) => {
      state.fetchProjectApiStatus = 'loading'
    })
    builder.addCase(fetchProject.fulfilled, (state, action) => {
      state.fetchProjectApiStatus = 'succeeded'
      state.projectData = action.payload
    })
    builder.addCase(fetchProject.rejected, (state, action) => {
      state.fetchProjectApiStatus = 'failed'
      state.error = action.error.message
    })
    builder.addCase(fetchFreelancerProject.pending, (state, action) => {
      state.fetchProjectApiStatus = 'loading'
    })
    builder.addCase(fetchFreelancerProject.fulfilled, (state, action) => {
      state.fetchProjectApiStatus = 'succeeded'
      state.projectData = action.payload
    })
    builder.addCase(fetchFreelancerProject.rejected, (state, action) => {
      state.fetchProjectApiStatus = 'failed'
      state.error = action.error.message
    })
    builder.addCase(fetchProjectChildren.pending, (state, action) => {
      state.fetchProjectChildrenApiStatus = 'loading'
    })
    builder.addCase(fetchProjectChildren.fulfilled, (state, action) => {
      state.fetchProjectChildrenApiStatus = 'succeeded'
      state.projectChildrenData = action.payload
    })
    builder.addCase(fetchProjectChildren.rejected, (state, action) => {
      state.fetchProjectChildrenApiStatus = 'failed'
      state.error = action.error.message
    })
    builder.addCase(fetchProjectTotals.pending, (state, action) => {
      state.fetchProjectTotalsApiStatus = 'loading'
    })
    builder.addCase(fetchProjectTotals.fulfilled, (state, action) => {
      state.fetchProjectTotalsApiStatus = 'succeeded'
      state.projectTotals = action.payload
    })
    builder.addCase(fetchProjectTotals.rejected, (state, action) => {
      state.fetchProjectTotalsApiStatus = 'failed'
      state.projectTotals = null
      state.error = action.error.message
    })
    builder.addCase(fetchFreelancerInfo.pending, (state, action) => {
      state.fetchFreelancerInfoApiStatus = 'loading'
    })
    builder.addCase(fetchFreelancerInfo.fulfilled, (state, action) => {
      state.fetchFreelancerInfoApiStatus = 'succeeded'
      state.freelanceEmail = [...action.payload]
    })
    builder.addCase(createProject.pending, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'loading'
    })
    builder.addCase(createProject.fulfilled, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'succeeded'
      state.projectModalOpenStatus = false
    })
    builder.addCase(createProject.rejected, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'failed'
      state.projectModalOpenStatus = false
      state.error = action.error?.message
      if (action.payload) {
        state.error = action.payload
      }
    })
    builder.addCase(updateProject.pending, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'loading'
    })
    builder.addCase(updateProject.fulfilled, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'succeeded'
      state.projectModalOpenStatus = false
    })
    builder.addCase(updateProject.rejected, (state, action) => {
      state.createOrUpdateProjectApiStatus = 'failed'
      state.projectModalOpenStatus = false
      state.error = action.error?.message
      if (action.payload) {
        state.error = action.payload
      }
    })
    builder.addCase(deleteProject.pending, (state, action) => {
      state.deleteProjectApiStatus = 'loading'
    })
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      state.deleteProjectApiStatus = 'succeeded'
      state.projectDeleteDialogOpenStatus = false
    })
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.deleteProjectApiStatus = 'failed'
      state.projectDeleteDialogOpenStatus = false
      state.error = action.error.message
    })
    builder.addCase(addFreelancerToProject.pending, (state, action) => {
      state.updateFreelancerInfoStatus = 'loading'
    })
    builder.addCase(addFreelancerToProject.fulfilled, (state, action) => {
      state.updateFreelancerInfoStatus = 'succeeded'
      state.isFreelancerModalOpen = false
    })
    builder.addCase(addFreelancerToProject.rejected, (state, action) => {
      state.updateFreelancerInfoStatus = 'failed'
      state.isFreelancerModalOpen = false
      state.error = action.error.message
    })
    builder.addCase(removeFreelancerFromProject.pending, (state, action) => {
      state.removeFreelancerStatus = 'loading'
    })
    builder.addCase(removeFreelancerFromProject.fulfilled, (state, action) => {
      state.removeFreelancerStatus = 'succeeded'
      state.isDeleteDialogOpen = false
    })
  },
})

export const {
  setUpdateCurrentProject,
  setIsDeleteDialogOpen,
  setProjectModalOpenStatus,
  setProjectDeleteDialogOpenStatus,
  sortProjects,
  sortProjectChildren,
  sortAndMegeProjectChildren,
  setSummaryQuoteId,
  setQuoteType,
  setIsFreelancersModalOpen,
  clearProjectsData,
  clearProjectsChildrenData,
} = projectSlice.actions

export default projectSlice.reducer as Reducer<ProjectState>
